import React, { useEffect, useState } from 'react';
import { ShoppingBag, ArrowRight, X, BellRing, MessageSquare, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NotificationItem } from '@/types/notification';

interface OrderAlertBannerProps {
  notification: NotificationItem | null;
  onDismiss: () => void;
}

export const OrderAlertBanner: React.FC<OrderAlertBannerProps> = ({
  notification,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!notification) {
      setProgress(100);
      return;
    }

    const duration = 12000; // 12 seconds
    const intervalTime = 100;
    const step = (intervalTime / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          onDismiss();
          return 0;
        }
        return prev - step;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [notification, onDismiss]);

  if (!notification) return null;

  const type = notification.type || '';
  const entityType = notification.entityType || '';

  const isLiveChat = entityType === 'chat' || type.includes('live_chat') || type.includes('chat');
  const isUserReg = type.includes('customer_registered') || type.includes('user_registered');
  const isOrder = entityType === 'order' || type.includes('order');

  let badgeText = '🔔 BİLDİRİM';
  let badgeStyle = 'bg-blue-500/20 text-blue-300 border-blue-500/40';
  let borderStyle = 'border-blue-500/80 ring-blue-500/20';
  let iconBg = 'from-blue-600 to-indigo-600 shadow-blue-500/30 ring-blue-400/30';
  let buttonBg = 'from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/30';
  let buttonText = 'Detayı Görüntüle';
  let IconComponent = BellRing;

  let handleAction = () => {
    navigate('/admin/notifications');
    onDismiss();
  };

  if (isLiveChat) {
    badgeText = type === 'live_chat_handover' ? '🚨 CANLI TEMSİLCİ TALEBİ' : '💬 CANLI DESTEK SOHBETİ';
    badgeStyle = 'bg-rose-500/20 text-rose-300 border-rose-500/40';
    borderStyle = 'border-rose-500/80 ring-rose-500/20';
    iconBg = 'from-rose-600 to-pink-600 shadow-rose-500/30 ring-rose-400/30';
    buttonBg = 'from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 shadow-rose-500/30';
    buttonText = 'Sohbete Git';
    IconComponent = MessageSquare;
    handleAction = () => {
      navigate('/admin/live-chat');
      onDismiss();
    };
  } else if (isUserReg) {
    badgeText = '👤 YENİ ÜYE KAYDI';
    badgeStyle = 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
    borderStyle = 'border-emerald-500/80 ring-emerald-500/20';
    iconBg = 'from-emerald-600 to-teal-600 shadow-emerald-500/30 ring-emerald-400/30';
    buttonBg = 'from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/30';
    buttonText = 'Müşterileri Görüntüle';
    IconComponent = UserPlus;
    handleAction = () => {
      navigate('/admin/customers');
      onDismiss();
    };
  } else if (isOrder) {
    badgeText = '🛒 YENİ SİPARİŞ';
    badgeStyle = 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40';
    borderStyle = 'border-indigo-500/80 ring-indigo-500/20';
    iconBg = 'from-indigo-600 to-purple-600 shadow-indigo-500/30 ring-indigo-400/30';
    buttonBg = 'from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-indigo-500/30';
    buttonText = 'Siparişi Görüntüle';
    IconComponent = ShoppingBag;
    handleAction = () => {
      const orderId = notification.orderId || notification.entityId;
      if (orderId) {
        navigate(`/admin/orders/${orderId}`);
      } else {
        navigate('/admin/orders');
      }
      onDismiss();
    };
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95vw] max-w-lg animate-slide-down select-none">
      <div className={`relative overflow-hidden rounded-2xl bg-slate-900 text-white shadow-2xl border-2 ring-4 backdrop-blur-xl ${borderStyle}`}>
        {/* Main Content */}
        <div className="p-4 sm:p-5 flex items-start gap-3.5 sm:gap-4">
          {/* Animated Icon Badge */}
          <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-tr text-white flex items-center justify-center shadow-lg ring-2 animate-pulse ${iconBg}`}>
            <IconComponent size={22} />
          </div>

          {/* Texts */}
          <div className="flex-1 min-w-0 pr-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${badgeStyle}`}>
                <BellRing size={12} />
                {badgeText}
              </span>
              {notification.orderNo && (
                <span className="text-xs font-mono font-bold text-indigo-300">
                  #{notification.orderNo}
                </span>
              )}
            </div>

            <h3 className="text-sm sm:text-base font-bold text-white mt-1.5 line-clamp-1">
              {notification.title || 'Yeni Bildirim'}
            </h3>

            <p className="text-xs text-slate-300 mt-0.5 line-clamp-2">
              {notification.body || 'Yeni bir bildirim aldınız.'}
            </p>

            {/* Action Button */}
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleAction}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r text-white text-xs font-bold shadow-md transition-transform active:scale-95 ${buttonBg}`}
              >
                <span>{buttonText}</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="Kapat"
          >
            <X size={18} />
          </button>
        </div>

        {/* Dismiss Progress Bar */}
        <div className="h-1 w-full bg-slate-800">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};
